import React, { useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '@/lib/auth-context';
import { getMarketplaceCatalog, getCoachMarketBalances, coachMarketPurchase } from '@/lib/coreApi';

type MarketItem = {
  id: string;
  sku: string;
  title: string;
  description?: string;
  previewUrl?: string;
  meta?: any;
  priceCredits: number | null;
  priceCents?: number | null;
  kind: string; // 'badge' | 'avatar' | 'theme' | ...
  owned?: boolean;
};

type CoachBalances = {
  coach_balance_credits: number;
  locker_room?: {
    id: string;
    name?: string;
    balance_credits: number;
  } | null;
};

export default function CoachMarketplace() {
  const { session } = useAuthContext() as any;
  const accessToken = session?.access_token;

  const [balances, setBalances] = useState<CoachBalances | null>(null);
  const [items, setItems] = useState<MarketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [spending, setSpending] = useState<string | null>(null);
  const [walletSource, setWalletSource] = useState<'personal' | 'locker_room'>('personal');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [balRes, catalogRes] = await Promise.all([
          getCoachMarketBalances(),
          getMarketplaceCatalog('coach'),
        ]);

        if (cancelled) return;

        setBalances(balRes?.data || null);

        const sectionItems: MarketItem[] = [];
        const sections = catalogRes?.data?.sections || {};
        Object.values(sections).forEach((list) => {
          if (Array.isArray(list)) {
            sectionItems.push(...list);
          }
        });
        setItems(sectionItems);

        if (balRes?.data?.locker_room) {
          setWalletSource('locker_room');
        }
      } catch (e) {
        console.error('Coach marketplace load failed', e);
        if (!cancelled) setError('Could not load marketplace.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const personalCredits = balances?.coach_balance_credits ?? 0;
  const lockerRoomCredits = balances?.locker_room?.balance_credits ?? 0;

  const effectiveBalance = useMemo(() => {
    return walletSource === 'locker_room' ? lockerRoomCredits : personalCredits;
  }, [lockerRoomCredits, personalCredits, walletSource]);

  async function handleBuy(item: MarketItem) {
    setSpending(item.id);
    setError(null);

    try {
      if (!accessToken) {
        throw new Error('You must be logged in to purchase items');
      }

      const res = await coachMarketPurchase({
        item_id: item.id,
        spend_source: walletSource,
        locker_room_id: walletSource === 'locker_room' ? (balances?.locker_room?.id || null) : null,
      });

      const data = res?.data || {};

      if (walletSource === 'locker_room' && balances?.locker_room) {
        setBalances((prev) =>
          prev
            ? {
                ...prev,
                locker_room: {
                  ...prev.locker_room!,
                  balance_credits:
                    typeof data.new_balance_credits === 'number'
                      ? data.new_balance_credits
                      : prev.locker_room!.balance_credits - (item.priceCredits || 0),
                },
              }
            : prev
        );
      } else {
        setBalances((prev) =>
          prev
            ? {
                ...prev,
                coach_balance_credits:
                  typeof data.new_balance_credits === 'number'
                    ? data.new_balance_credits
                    : prev.coach_balance_credits - (item.priceCredits || 0),
              }
            : prev
        );
      }

      setItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, owned: true } : p)));
    } catch (e: any) {
      console.error('Coach buy failed', e);
      setError(e.message || 'Could not complete purchase.');
    } finally {
      setSpending(null);
    }
  }

  const cosmetics = useMemo(
    () =>
      items.filter((i) =>
        ['badge', 'avatar', 'theme', 'border', 'digital_good'].includes(i.kind)
      ),
    [items]
  );

  const creditPacks = useMemo(
    () => items.filter((i) => i.kind === 'credit_pack'),
    [items]
  );

  function ItemCard({ item }: { item: MarketItem }) {
    const price = item.priceCredits ?? 0;
    const disabled = !!item.owned || effectiveBalance < price || spending === item.id;

    return (
      <div className="border rounded-lg p-3 flex flex-col gap-2 bg-slate-50">
        {item.previewUrl && (
          <img
            src={item.previewUrl}
            alt={item.title}
            className="w-full h-24 object-cover rounded-md"
          />
        )}
        <div className="text-xs uppercase tracking-wide text-slate-500">{item.kind}</div>
        <div className="font-medium text-sm">{item.title}</div>
        {item.description && (
          <div className="text-xs text-slate-600 line-clamp-2">{item.description}</div>
        )}
        <div className="mt-auto flex items-center justify-between text-xs">
          <span>{price} credits</span>
          <button
            disabled={disabled}
            onClick={() => handleBuy(item)}
            className="px-2 py-1 text-xs rounded border disabled:opacity-50"
          >
            {item.owned ? 'Owned' : spending === item.id ? 'Buying…' : 'Buy'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold">Coach Marketplace</h1>
          <p className="text-xs text-slate-600">
            Decorate your profile & locker room, or top up credits.
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Spend from:</span>
            <select
              value={walletSource}
              onChange={(e) => setWalletSource(e.target.value as 'personal' | 'locker_room')}
              className="border rounded px-2 py-1 text-xs bg-white"
            >
              <option value="personal">Coach credits ({personalCredits} cr)</option>
              {balances?.locker_room && (
                <option value="locker_room">
                  Locker room ({balances.locker_room.name || 'Locker room'} – {lockerRoomCredits} cr)
                </option>
              )}
            </select>
          </div>
          <div className="text-xs text-slate-500">Available: {effectiveBalance} credits</div>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading marketplace…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500">
          No marketplace items are available right now.
        </div>
      ) : (
        <>
          {cosmetics.length > 0 && (
            <section>
              <h2 className="text-sm font-medium mb-2">Cosmetics</h2>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                {cosmetics.map((i) => (
                  <ItemCard key={i.id} item={i} />
                ))}
              </div>
            </section>
          )}

          {creditPacks.length > 0 && (
            <section className="mt-6">
              <h2 className="text-sm font-medium mb-2">Credit packs</h2>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                {creditPacks.map((i) => (
                  <ItemCard key={i.id} item={i} />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
